import { useSuspenseQueries } from '@tanstack/react-query';
import { assert } from 'assert-ts';
import { lighthouseCountQueryOptions } from '#api/lighthouses.queries.ts';
import { useSuspenseManagedLighthouses } from '#api/managed-lighthouses.queries.ts';
import { relayCountQueryOptions } from '#api/relays.queries.ts';
import type { Network } from '#dn-types/network.ts';

/**
 * A network is "covered" by managed lighthouses when it has a GET /v1/managed-lighthouses entry
 * (one per network with an active tenancy, from the shared org-wide fetch — opted-out networks,
 * ineligible v1 networks, and disabled tenancies are absent). A pending tenancy still counts as
 * covered: provisioning is transient (it resolves on the fleet's next check-in, and a re-enabled
 * tenancy already holds its reserved address), so coverage warnings and dialogs must not flag it
 * as a gap — the networks settings pages surface the "Pending" status instead. Covered networks
 * relay (and have a lighthouse) via managed lighthouses, so they never appear in a coverage gap
 * and we skip their per-network host queries.
 *
 * Networks are capped at 10 per account, so fanning out a `pageSize:1` count query per candidate
 * network is bounded — no max-page fetch needed.
 */
function useIsMtlhCovered() {
  const managedLighthouses = useSuspenseManagedLighthouses();
  const coveredNetworkIDs = new Set(managedLighthouses.map((lighthouse) => lighthouse.networkID));
  return (network: Network) => coveredNetworkIDs.has(network.id);
}

/**
 * {@link useUncoveredRelayNetworks} returns the networks with NO relay coverage: not covered by
 * managed lighthouses, not relaying through a self-hosted lighthouse (the `lighthousesAsRelays`
 * setting only counts if the network actually has a lighthouse), and with no dedicated relay host.
 */
export function useUncoveredRelayNetworks(networks: Network[]): Network[] {
  const isMtlhCovered = useIsMtlhCovered();
  const candidates = networks.filter((network) => !isMtlhCovered(network));

  // One interleaved query set (relay, lighthouse, relay, …) keeps all counts fanning out in a single
  // parallel batch — two separate useSuspenseQueries calls would waterfall (the second suspends
  // behind the first). Results de-interleave by index below.
  const results = useSuspenseQueries({
    queries: candidates.flatMap((network) => [
      relayCountQueryOptions({ networkID: network.id }),
      lighthouseCountQueryOptions({ networkID: network.id }),
    ]),
  });

  return candidates.filter((network, i) => {
    // De-interleave the pair above: each candidate's relay count is at 2i, lighthouse count at 2i+1.
    // `useSuspenseQueries` guarantees a settled `data` (errors throw to the boundary), so assert the
    // in-range index rather than masking a "missing" count as 0.
    const hasRelay = assert(results[i * 2]).data > 0;
    const hasLighthouse = assert(results[i * 2 + 1]).data > 0;
    const relaysViaSelfHostedLighthouse = network.lighthousesAsRelays && hasLighthouse;
    return !relaysViaSelfHostedLighthouse && !hasRelay;
  });
}

/**
 * {@link useUncoveredLighthouseNetworks} returns the networks with NO lighthouse: not covered by
 * managed lighthouses and with no self-hosted lighthouse host.
 */
export function useUncoveredLighthouseNetworks(networks: Network[]): Network[] {
  const isMtlhCovered = useIsMtlhCovered();
  const candidates = networks.filter((network) => !isMtlhCovered(network));

  const results = useSuspenseQueries({
    queries: candidates.map((network) => lighthouseCountQueryOptions({ networkID: network.id })),
  });

  return candidates.filter((_network, i) => assert(results[i]).data === 0);
}
